import Quill from 'quill';
import { MutableRefObject, useEffect, useRef } from 'react';

import { plainTextToQuillHtml, quillHtmlToPlainText } from './plainTextHtml';
import {
    addSignature,
    containsSignature,
    isEmptyHtml,
    normalizeMergeFieldBlots,
} from './signatureUtils';

export type ContentInsertMode = 'innerHTML' | 'paste';

type UseSignatureOptions = {
    quillRef: MutableRefObject<Quill | null>;
    lastValueRef: MutableRefObject<string>;
    value: string;
    signature?: string;
    onChange: (value: string) => void;
    contentInsertMode?: ContentInsertMode;
    plainTextOutput?: boolean;
};

export const useSignature = ({
    quillRef,
    lastValueRef,
    value,
    signature,
    onChange,
    contentInsertMode = 'innerHTML',
    plainTextOutput,
}: UseSignatureOptions) => {
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;

    useEffect(() => {
        const quill = quillRef.current;
        if (
            !quill ||
            (value === lastValueRef.current &&
                (!signature ||
                    isEmptyHtml(value) ||
                    containsSignature(value, signature)))
        ) {
            return;
        }

        const htmlValue = plainTextOutput ? plainTextToQuillHtml(value) : value;
        const contentWithSignature = addSignature(
            normalizeMergeFieldBlots(htmlValue),
            signature,
        );
        if (contentInsertMode === 'innerHTML') {
            quill.root.innerHTML = contentWithSignature;
        } else {
            quill.clipboard.dangerouslyPasteHTML(contentWithSignature, 'api');
        }

        const externalValue = plainTextOutput
            ? quillHtmlToPlainText(contentWithSignature)
            : contentWithSignature;
        lastValueRef.current = externalValue;

        if (externalValue !== value) {
            onChangeRef.current(externalValue);
        }
    }, [value, signature]);
};
